import Grid from '@mui/material/Grid';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import styles from './AddMenu.module.css';
import * as Api from '../../../../../api';
import AlertMsg from '../../../../../components/AlertMsg';
import Container from '../../../../../components/Container';
import Loader from '../../../../../components/Loader';
import RestaurantHeader from '../../../../../components/RestaurantHeader';
import { useGetMenuItem } from '../services';
import type { TAlertState, TMenuItem, TRestaurantDetail } from '../schemas';
import AddSettingItem from './AddSettingItem';

type TRestaurantData = TRestaurantDetail & {
  currecnyIcon?: string;
};

type TModelData = {
  modelPath?: { glb?: string };
};

type TApiResponse<T> = {
  success: boolean;
  data: T;
  msg: string;
};

const AddMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const params = location.pathname.split('/');
  const restaurantId = params.length >= 4 ? params[2] : '';
  const menuId = params.length >= 5 ? params[4] : '';

  const [restaurantLoading, setRestaurantLoading] = useState(true);
  const [selectedTab] = useState('edit_menu');
  const [modelData, setModelData] = useState<TModelData | null>(null);
  const [restaurantDetail, setRestaurantDetail] = useState<TRestaurantData | null>(null);
  const [msgAlert, setMsgAlert] = useState<TAlertState>({
    open: false,
    message: '',
    msgType: 'error',
  });

  const { data: rawMenuItem, isLoading: menuItemLoading } = useGetMenuItem(menuId, restaurantId);

  // API returns images as URL strings — convert to TMenuItemImage shape for AddSettingItem
  const menuData: TMenuItem | null = rawMenuItem
    ? {
        ...rawMenuItem,
        images: (rawMenuItem.images as unknown as string[]).map((src) => {
          const index = src.lastIndexOf('/') + 1;
          const filename = src.substring(index);
          return { name: filename, image: src, upload: true };
        }),
      }
    : null;

  const isLoading = restaurantLoading || (!!menuId && menuItemLoading);

  useEffect(() => {
    Api.getRestaurantData(restaurantId).then((response: TApiResponse<TRestaurantData>) => {
      if (response.success) {
        setRestaurantDetail(response.data);
      } else {
        setMsgAlert({ open: true, message: response.msg, msgType: 'error' });
      }
      setRestaurantLoading(false);
    });
  }, []);

  useEffect(() => {
    if (rawMenuItem?.modelId) {
      Api.getArModelData(rawMenuItem.modelId).then((response: TApiResponse<TModelData>) => {
        if (response.success) {
          setModelData(response.data);
        } else {
          setMsgAlert({ open: true, message: response.msg, msgType: 'error' });
        }
      });
    }
  }, [rawMenuItem?.modelId]);

  return (
    <Container page={'restaurants'} searchVal={''} onSearch={() => null} searchLabel=''>
      {isLoading ? (
        <Loader height={{ height: window.innerHeight - 150 }} />
      ) : (
        <Grid container item xs={12}>
          <Grid item xs={12} mb={2}>
            <RestaurantHeader
              title={`${menuData ? 'Edit' : 'Add'} Menu`}
              onBackBtnClick={() => navigate('/restaurant/' + restaurantId + '/settings')}
            />
          </Grid>
          <Grid item xs={12} className={styles.mainView} p={2} pt={3}>
            <AddSettingItem
              itemData={menuData}
              restaurantId={restaurantId}
              selectedTab={selectedTab}
              modelData={modelData ?? undefined}
              currencyIcon={restaurantDetail?.currecnyIcon}
              restaurantDetail={restaurantDetail ?? undefined}
              onCancel={() =>
                navigate(
                  menuData?._idCategory
                    ? `/restaurant/${restaurantId}/settings#${menuData._idCategory}`
                    : `/restaurant/${restaurantId}/settings`,
                )
              }
              onSubmit={(categoryId) => {
                const targetCategoryId = categoryId || menuData?._idCategory;
                navigate(
                  targetCategoryId
                    ? `/restaurant/${restaurantId}/settings#${targetCategoryId}`
                    : `/restaurant/${restaurantId}/settings`,
                );
              }}
            />
          </Grid>
        </Grid>
      )}
      <AlertMsg
        msgAlert={msgAlert}
        onCloseAlertMsg={() => setMsgAlert({ open: false, message: '', msgType: 'error' })}
      />
    </Container>
  );
};

export default AddMenu;
